import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEmailMarketingV1AccountDetails from './email-marketing-v1-account-details-account-details.http.js';

/**
 * An account can be active or in some state of suspension (warned/suspended/banned).
 * If a user is in a state of suspension they must visit their email marketing account in the dashboard and follow the instructions.
 */
export interface AccountDetails {
  /** Account status. */
  status?: EnumWithLiterals;
  /** Current premium package information. */
  package?: Package;
  /** Quota period and usage. */
  quotaPeriod?: QuotaPeriod;
  /** Max number of recipients for an email marketing campaign. Exceeding this limit may lead to campaigns being rejected. */
  maxCampaignAudienceSize?: number;
}

export enum Enum {
  /** Able to use email marketing normally. */
  ACTIVE = 'ACTIVE',
  /** Must explicitly agree to terms of use to activate the account. */
  WARNED = 'WARNED',
  /** Must fill out compliance questionnaire, unable to use email marketing. */
  SUSPENDED = 'SUSPENDED',
  /** Must fill out compliance questionnaire, unable to use email marketing. */
  BANNED = 'BANNED',
  /** Must fill out compliance questionnaire to activate the account. */
  SUSPENDED_AUTOLIFT = 'SUSPENDED_AUTOLIFT',
}

/** @enumType */
export type EnumWithLiterals =
  | Enum
  | 'ACTIVE'
  | 'WARNED'
  | 'SUSPENDED'
  | 'BANNED'
  | 'SUSPENDED_AUTOLIFT';

export interface Package {
  /** Package ID. */
  _id?: string;
  /** Package group. */
  group?: string;
  /** Allocated quota per month. */
  monthlyQuotaAllocation?: Quota;
}

export interface Quota {
  /** Number of allocated marketing campaigns per month (-1 means unlimited). */
  campaigns?: number;
  /** Number of allocated individual emails per month. */
  emails?: number;
}

export enum BillingCycle {
  UNKNOWN = 'UNKNOWN',
  TWO_YEARS = 'TWO_YEARS',
  YEARLY = 'YEARLY',
  MONTHLY = 'MONTHLY',
}

/** @enumType */
export type BillingCycleWithLiterals =
  | BillingCycle
  | 'UNKNOWN'
  | 'TWO_YEARS'
  | 'YEARLY'
  | 'MONTHLY';

export interface QuotaPeriod {
  /** Current quota period start date. */
  dateFrom?: Date | null;
  /** Current quota period end date - after which quota will roll over. */
  dateTo?: Date | null;
  /** Information about allocated quota already used in this period. */
  quotaUsage?: Quota;
}

export interface Features {
  multipleSenderDetails?: boolean;
  removeWixBranding?: boolean;
  scheduling?: boolean;
}

export enum Rank {
  /** No reputation has been established (new site or didn't have enough traffic in the recent time period). */
  NEW = 'NEW',
  /** Has traffic and it's quality is good. */
  GOOD = 'GOOD',
  /** Has traffic but it's quality is bad. */
  BAD = 'BAD',
}

/** @enumType */
export type RankWithLiterals = Rank | 'NEW' | 'GOOD' | 'BAD';

export interface GetAccountDetailsRequest {}

export interface GetAccountDetailsResponse {
  /** Current account details. */
  accountDetails?: AccountDetails;
}

export interface GetComplianceDetailsRequest {}

export interface GetComplianceDetailsResponse {
  /** Current compliance details. */
  complianceDetails?: ComplianceDetails;
}

export interface ComplianceDetails
  extends ComplianceDetailsProductOptionsOneOf {
  emailMarketingOptions?: EmailMarketingOptions;
  automationOptions?: AutomationOptions;
  /** Account status. */
  status?: EnumWithLiterals;
  /**
   * Suspension reasons.
   * @maxSize 10
   * @maxLength 100
   */
  suspensionReasons?: string[];
  /**
   * Support ticket id.
   * @format GUID
   */
  supportTicketId?: string | null;
  /** product */
  product?: ProductWithLiterals;
  /**
   * reasons for the restriction action
   * @maxSize 10
   */
  restrictionReasons?: RestrictionReason[];
}

/** @oneof */
export interface ComplianceDetailsProductOptionsOneOf {
  emailMarketingOptions?: EmailMarketingOptions;
  automationOptions?: AutomationOptions;
}

export enum Product {
  UNSPECIFIED = 'UNSPECIFIED',
  EMAIL_MARKETING = 'EMAIL_MARKETING',
  AUTOMATION = 'AUTOMATION',
}

/** @enumType */
export type ProductWithLiterals =
  | Product
  | 'UNSPECIFIED'
  | 'EMAIL_MARKETING'
  | 'AUTOMATION';

export interface EmailMarketingOptions {
  /** @format GUID */
  campaignId?: string | null;
}

export interface AutomationOptions {
  /** @format GUID */
  automationId?: string | null;
  /** @format GUID */
  campaignId?: string | null;
}

export interface RestrictionReason extends RestrictionReasonTypeOneOf {
  content?: ContentWithLiterals;
  senderReputation?: SenderReputationWithLiterals;
  statistics?: StatisticsWithLiterals;
  value?: string | null;
}

/** @oneof */
export interface RestrictionReasonTypeOneOf {
  content?: ContentWithLiterals;
  senderReputation?: SenderReputationWithLiterals;
  statistics?: StatisticsWithLiterals;
}

export enum Content {
  TEXT = 'TEXT',
  LINK = 'LINK',
  IMAGE = 'IMAGE',
  EMAIL_SUBJECT = 'EMAIL_SUBJECT',
  SENDER_NAME = 'SENDER_NAME',
  SENDER_EMAIL = 'SENDER_EMAIL',
}

/** @enumType */
export type ContentWithLiterals =
  | Content
  | 'TEXT'
  | 'LINK'
  | 'IMAGE'
  | 'EMAIL_SUBJECT'
  | 'SENDER_NAME'
  | 'SENDER_EMAIL';

export enum SenderReputation {
  DOMAIN_BLOCKLISTED = 'DOMAIN_BLOCKLISTED',
  SENDER_EMAIL_PATTERN = 'SENDER_EMAIL_PATTERN',
  SENDER_EMAIL_DISPOSABLE = 'SENDER_EMAIL_DISPOSABLE',
  SENDER_EMAIL_UNDELIVERABLE = 'SENDER_EMAIL_UNDELIVERABLE',
  DNS_RECORDS_INCOMPLETE = 'DNS_RECORDS_INCOMPLETE',
  OTHER = 'OTHER',
}

/** @enumType */
export type SenderReputationWithLiterals =
  | SenderReputation
  | 'DOMAIN_BLOCKLISTED'
  | 'SENDER_EMAIL_PATTERN'
  | 'SENDER_EMAIL_DISPOSABLE'
  | 'SENDER_EMAIL_UNDELIVERABLE'
  | 'DNS_RECORDS_INCOMPLETE'
  | 'OTHER';

export enum Statistics {
  SPAM_COMPLAINTS = 'SPAM_COMPLAINTS',
  BOUNCES = 'BOUNCES',
  LOW_ENGAGEMENT = 'LOW_ENGAGEMENT',
  SPAM_TRAPS = 'SPAM_TRAPS',
}

/** @enumType */
export type StatisticsWithLiterals =
  | Statistics
  | 'SPAM_COMPLAINTS'
  | 'BOUNCES'
  | 'LOW_ENGAGEMENT'
  | 'SPAM_TRAPS';

export interface RespondRequest {
  action?: ActionWithLiterals;
  supportTicketData?: SupportTicketData;
}

export enum Action {
  AGREE_TO_TERMS = 'AGREE_TO_TERMS',
  OPEN_TICKET = 'OPEN_TICKET',
  FILL_FORM = 'FILL_FORM',
  ANSWER_QUESTIONS = 'ANSWER_QUESTIONS',
  FILL_AUTOLIFT_FORM = 'FILL_AUTOLIFT_FORM',
  FILL_SHORT_FORM = 'FILL_SHORT_FORM',
  FILL_SHORT_AUTOLIFT_FORM = 'FILL_SHORT_AUTOLIFT_FORM',
}

/** @enumType */
export type ActionWithLiterals =
  | Action
  | 'AGREE_TO_TERMS'
  | 'OPEN_TICKET'
  | 'FILL_FORM'
  | 'ANSWER_QUESTIONS'
  | 'FILL_AUTOLIFT_FORM'
  | 'FILL_SHORT_FORM'
  | 'FILL_SHORT_AUTOLIFT_FORM';

export interface SupportTicketData {
  /** @maxLength 200 */
  subject?: string;
  /** @maxLength 20000 */
  body?: string;
  /** @maxLength 20000 */
  agentReplyBody?: string | null;
}

export interface RespondResponse {
  /**
   * ID of the support ticket created (optional).
   * @format GUID
   */
  supportTicketId?: string | null;
}

/**
 * Retrieves email marketing account details.
 * @public
 * @permissionId Shoutout.Manage
 * @applicableIdentity APP
 * @fqn wix.emailmarketing.api.v1.AccountDetailsService.Get
 */
export async function getAccountDetails(): Promise<
  NonNullablePaths<
    GetAccountDetailsResponse,
    | `accountDetails.status`
    | `accountDetails.package._id`
    | `accountDetails.package.group`
    | `accountDetails.package.monthlyQuotaAllocation.campaigns`
    | `accountDetails.package.monthlyQuotaAllocation.emails`
    | `accountDetails.maxCampaignAudienceSize`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixEmailMarketingV1AccountDetails.get(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
